import { FC, useEffect } from 'react';

import { AnimatePresence, motion } from 'motion/react';
import { twMerge } from 'tailwind-merge';

import { ArrowIcon, CloseIcon, SecurityShieldImage } from '../../../assets';
import { ANIMATION_VARIANTS } from '../../../config';
import { useToastsStore } from '../../../lib';
import { Button } from '../../button';
import { Support } from '../../components/support';
import { TOnboardingTemplate } from './types';

export const OnboardingTemplate: FC<TOnboardingTemplate> = ({
  title,
  description,
  HeaderContent,
  TopContent,
  onBack,
  onClose,
  BottomContent,
  buttonProps,
  isShowRightSide = false,
  classNames = {},
  className,
  children,
  isHiddenSupport = false,
  mode = 'wait',
}) => {
  const isHasHeader = Boolean(HeaderContent || onBack || onClose);
  const setIsInWelcomeShell = useToastsStore(
    (state) => state.setIsInWelcomeShell,
  );

  useEffect(() => {
    if (isShowRightSide) setIsInWelcomeShell(true);
    return () => {
      setIsInWelcomeShell(false);
    };
  }, []);

  return (
    <AnimatePresence mode={mode}>
      <motion.div
        key={title}
        {...ANIMATION_VARIANTS.fade}
        className={twMerge(
          'relative flex h-dvh flex-col overflow-hidden',
          isShowRightSide && 'flex-row',
          classNames.wrapper,
        )}
      >
        {isHasHeader && (
          <div
            className={twMerge(
              'fixed left-0 top-0 z-10 w-full px-5 py-[14px]',
              classNames.header,
            )}
          >
            {HeaderContent ||
              ((onBack || onClose) && (
                <div className="flex items-center">
                  {onBack && (
                    <Button
                      size="icon"
                      variant="secondary-foreground"
                      onClick={onBack}
                      className="h-12 w-12 rounded-full"
                    >
                      <ArrowIcon className="!size-6" />
                    </Button>
                  )}
                  {onClose && (
                    <Button
                      size="icon"
                      variant="secondary-foreground"
                      onClick={onClose}
                      className="ml-auto h-12 w-12 rounded-full"
                    >
                      <CloseIcon className="!size-6" />
                    </Button>
                  )}
                </div>
              ))}
          </div>
        )}
        <div
          className={twMerge(
            'relative max-h-dvh flex-1 overflow-y-auto px-5 onboarding-lg:px-[60px] onboarding-lg:pt-[50px]',
            isHasHeader && 'pt-[76px] onboarding-lg:pt-[96px]',
            classNames.container,
          )}
        >
          <div
            className={twMerge(
              'mx-auto flex h-full w-full flex-col items-center text-center',
              classNames.content,
            )}
          >
            {TopContent}

            <h1
              className={twMerge(
                'heading-m onboarding-lg:heading-xl',
                isShowRightSide && 'max-w-[335px] onboarding-lg:max-w-[350px]',
                classNames.title,
              )}
            >
              {title}
            </h1>

            {description && (
              <p
                className={twMerge(
                  'mt-4 typography-14m lg:mt-5 lg:typography-16m',
                  isShowRightSide &&
                    'max-w-[335px] onboarding-lg:max-w-[350px]',
                  classNames.description,
                )}
              >
                {description}
              </p>
            )}
            <div
              className={twMerge(
                'mt-8 w-full max-w-[335px] onboarding-lg:mt-10 onboarding-lg:max-w-[350px]',
                className,
              )}
            >
              {children}
            </div>
            {(BottomContent || buttonProps) && (
              <div
                className={twMerge(
                  'sticky bottom-0 mt-auto w-full bg-welcome-footer',
                  !BottomContent && 'pb-5 onboarding-lg:pb-6',
                  classNames.bottom,
                )}
              >
                <div className="mx-auto max-w-[335px] onboarding-lg:max-w-[350px]">
                  {buttonProps && (
                    <Button
                      {...buttonProps}
                      size={buttonProps.size ?? 'lg'}
                      className={twMerge('mt-5 w-full', buttonProps.className)}
                    >
                      {buttonProps.text ?? 'Continue'}
                    </Button>
                  )}
                  {BottomContent}
                </div>
              </div>
            )}
          </div>
        </div>

        {isShowRightSide && (
          <div
            className={twMerge(
              'relative flex hidden min-w-[50%] flex-1 justify-center overflow-hidden bg-welcome-hero bg-cover bg-no-repeat onboarding-lg:block',
              classNames.rightSide,
            )}
          >
            <img
              src={SecurityShieldImage}
              alt="security shield"
              className="mx-auto mt-[183px] max-h-[447px] max-w-[417px]"
            />

            <p className="absolute bottom-[30px] w-full text-center text-base-100 typography-14r">
              High-level security protocols
            </p>
          </div>
        )}
        {!isHiddenSupport && <Support />}
      </motion.div>
    </AnimatePresence>
  );
};
